import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { createAssignment, getMyClasses, generateRubric, createRubric, getRubrics } from '../../lib/api';
import { CCSS_ELA_STANDARDS, CCSS_ELA_BY_GRADE, CCSS_STANDARDS, GRADE_BANDS } from '../../lib/standards';
import { parseRubricText } from '../../lib/rubricParser';
import { DemoChip } from '../ui/demo-chip';

// Re-export for backwards compat (FlowRubric.js imports from here)
export { CCSS_STANDARDS };

// Default due date for template pre-fills: 7 days out, formatted for datetime-local (local time)
const defaultDueDate = () => {
  const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Deep-copy a template rubric so in-modal edits never mutate the exported template object
const cloneRubric = (rubric) => {
  if (!rubric || !Array.isArray(rubric.criteria)) return null;
  return {
    title: rubric.title || '',
    criteria: rubric.criteria.map(c => ({
      criterion: c.criterion,
      levels: (c.levels || []).map(l => ({ ...l })),
    })),
  };
};

export const CreateAssignmentModal = ({ onClose, onCreated, template = null }) => {
  const [title, setTitle] = useState(template?.title || '');
  const [prompt, setPrompt] = useState(template?.prompt || '');
  const [standards, setStandards] = useState(template?.standards ? [...template.standards] : []);
  const [dueDate, setDueDate] = useState(template ? defaultDueDate() : '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedStandard, setExpandedStandard] = useState(null);
  const [gradeBand, setGradeBand] = useState(template?.gradeBand || '9-10');
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [reviewMode, setReviewMode] = useState('review');
  const [rubric, setRubric] = useState(() => cloneRubric(template?.rubric));
  const [expandedCriterion, setExpandedCriterion] = useState(null);
  const [isGeneratingRubric, setIsGeneratingRubric] = useState(false);
  const [savedRubrics, setSavedRubrics] = useState([]);

  // Teacher's classes for targeting — preselect when there's exactly one
  useEffect(() => {
    const loadClasses = async () => {
      try {
        const data = await getMyClasses();
        const cls = Array.isArray(data) ? data : data?.classes || [];
        setClasses(cls);
        if (cls.length === 1) setClassId(cls[0].id);
      } catch (e) {
        console.error('Failed to load classes:', e);
      }
    };
    loadClasses();

    // Saved rubrics for the "reuse" path (master spec Phase 2 item 2:
    // build manually, generate with AI, or reuse). Non-fatal on failure.
    const loadSavedRubrics = async () => {
      try {
        const data = await getRubrics();
        setSavedRubrics((Array.isArray(data) ? data : []).filter(r => Array.isArray(r.criteria) && r.criteria.length > 0));
      } catch (e) {
        setSavedRubrics([]);
      }
    };
    loadSavedRubrics();
  }, []);

  const handleReuseRubric = (rubricId) => {
    const saved = savedRubrics.find(r => r.id === rubricId);
    if (!saved) return;
    setRubric(cloneRubric({ title: saved.title, criteria: saved.criteria }));
    setExpandedCriterion(null);
    toast.success('Rubric loaded — edit anything before you save.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !prompt) {
      toast.error('Please fill in title and prompt');
      return;
    }
    if (classes.length > 0 && !classId) {
      toast.error('Choose which class this assignment is for');
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await createAssignment({
        title,
        prompt,
        ccss_standards: standards,
        due_date: dueDate || null,
        class_id: classId || null,
        review_mode: reviewMode
      });

      // Attach the rubric to the new assignment (teacher_id comes from auth on the backend)
      if (rubric && rubric.criteria?.length > 0) {
        try {
          await createRubric({
            standard_code: standards[0] || `W.${gradeBand}.1`,
            title: rubric.title?.trim() || `${title} Rubric`,
            criteria: rubric.criteria,
            assignment_id: created?.id || null,
          });
        } catch (rubricError) {
          toast.warning("Assignment created, but the rubric didn't save — add it from the Rubrics tab.");
          onCreated();
          return;
        }
      }

      toast.success('Assignment created!');
      onCreated();
    } catch (error) {
      toast.error('Failed to create assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateRubric = async () => {
    if (!prompt.trim()) {
      toast.error('Write the assignment prompt first — the AI uses it to build the rubric');
      return;
    }
    setIsGeneratingRubric(true);
    try {
      const genStandard = standards[0] || `W.${gradeBand}.1`;
      const result = await generateRubric(genStandard, prompt);
      const parsed = parseRubricText(result.rubric);
      if (!parsed || parsed.length === 0) {
        toast.error('Could not parse the generated rubric. Try again.');
        return;
      }
      setRubric({
        title: title.trim() ? `${title.trim()} Rubric` : `${genStandard} Rubric`,
        criteria: parsed,
      });
      setExpandedCriterion(0);
      toast.success('Rubric generated! Edit any criterion below.');
    } catch (error) {
      console.error('Rubric generation error:', error);
      const status = error.response?.status;
      toast.error(status === 502 || status === 503
        ? 'The AI service had trouble generating a rubric. Try again in a moment.'
        : 'Failed to generate rubric. Check your connection and try again.');
    } finally {
      setIsGeneratingRubric(false);
    }
  };

  const updateRubricCriterionName = (idx, name) => {
    setRubric(prev => ({
      ...prev,
      criteria: prev.criteria.map((c, i) => i === idx ? { ...c, criterion: name } : c),
    }));
  };

  const updateRubricLevelDescription = (cIdx, lIdx, description) => {
    setRubric(prev => ({
      ...prev,
      criteria: prev.criteria.map((c, i) => i === cIdx
        ? { ...c, levels: c.levels.map((l, j) => j === lIdx ? { ...l, description } : l) }
        : c),
    }));
  };

  const toggleExpand = (stdCode) => {
    setExpandedStandard(prev => prev === stdCode ? null : stdCode);
  };

  const gradeStandards = CCSS_ELA_BY_GRADE[gradeBand] || {};
  const gradeStandardCodes = Object.keys(gradeStandards);

  const getSubCodes = (stdCode) => {
    const std = gradeStandards[stdCode] || CCSS_ELA_STANDARDS[stdCode];
    return std ? Object.keys(std.subs) : [];
  };

  const hasSubs = (stdCode) => {
    return getSubCodes(stdCode).length > 0;
  };

  const areAllSubsSelected = (stdCode) => {
    const subs = getSubCodes(stdCode);
    if (subs.length === 0) return standards.includes(stdCode);
    return subs.every(sub => standards.includes(sub));
  };

  const areSomeSubsSelected = (stdCode) => {
    const subs = getSubCodes(stdCode);
    if (subs.length === 0) return standards.includes(stdCode);
    return subs.some(sub => standards.includes(sub));
  };

  const toggleAnchorStandard = (stdCode) => {
    const subs = getSubCodes(stdCode);
    if (subs.length === 0) {
      // No sub-standards — toggle the anchor code itself
      setStandards(prev =>
        prev.includes(stdCode)
          ? prev.filter(s => s !== stdCode)
          : [...prev, stdCode]
      );
      return;
    }
    // Has sub-standards — toggle all subs
    if (areAllSubsSelected(stdCode)) {
      setStandards(prev => prev.filter(s => !subs.includes(s)));
    } else {
      setStandards(prev => {
        const withoutSubs = prev.filter(s => !subs.includes(s));
        return [...withoutSubs, ...subs];
      });
    }
  };

  const toggleSubStandard = (subCode) => {
    setStandards(prev =>
      prev.includes(subCode)
        ? prev.filter(s => s !== subCode)
        : [...prev, subCode]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 max-h-[80vh] overflow-y-auto"
      >
        <h2 className="font-heading text-xl font-semibold text-white mb-6">Create Assignment</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-slate-400 text-sm mb-1">Title</label>
            <input
              data-testid="assignment-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Persuasive Essay on Climate Change"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white"
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-1">Assign to Class</label>
            {classes.length > 0 ? (
              <select
                data-testid="assignment-class-select"
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white"
              >
                <option value="">Select a class…</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.class_name}</option>
                ))}
              </select>
            ) : (
              <p className="text-slate-500 text-sm bg-slate-900 border border-slate-700 rounded-lg px-4 py-2">
                No classes yet — create one in Settings or import from Google Classroom.
                You can still create this assignment and share its link directly.
              </p>
            )}
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-1">Writing Prompt</label>
            <textarea
              data-testid="assignment-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder="Write the assignment prompt here..."
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white resize-none"
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-1">Grade Level</label>
            <select
              value={gradeBand}
              onChange={(e) => setGradeBand(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white mb-3"
            >
              {GRADE_BANDS.map(band => (
                <option key={band} value={band}>
                  {band === '9-10' || band === '11-12' ? `Grades ${band}` : `Grade ${band}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-1">CCSS Standards</label>
            <div className="space-y-1">
              {gradeStandardCodes.map(stdCode => {
                const std = gradeStandards[stdCode];
                const expanded = expandedStandard === stdCode;
                const allSelected = areAllSubsSelected(stdCode);
                const someSelected = areSomeSubsSelected(stdCode);
                const subCodes = getSubCodes(stdCode);
                const hasSubStandards = subCodes.length > 0;

                return (
                  <div key={stdCode} className="rounded-lg overflow-hidden">
                    {/* Anchor standard row */}
                    <div className="flex items-center gap-2">
                      {hasSubStandards && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(stdCode)}
                          className="text-flow-blue-hover hover:text-flow-blue-hover/80 transition-colors flex-shrink-0"
                        >
                          {expanded
                            ? <ChevronDown size={16} />
                            : <ChevronRight size={16} />
                          }
                        </button>
                      )}
                      {!hasSubStandards && <div className="w-4 flex-shrink-0" />}
                      <button
                        type="button"
                        onClick={() => toggleAnchorStandard(stdCode)}
                        className={`flex-1 text-left px-3 py-2 rounded text-xs font-mono transition-colors ${
                          allSelected
                            ? 'bg-ai-accent text-white'
                            : someSelected
                              ? 'bg-ai-accent/40 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        <span className="font-semibold">{stdCode}</span>
                        <span className="ml-2 text-[10px] opacity-75 font-sans">{std.title.slice(0, 80)}{std.title.length > 80 ? '...' : ''}</span>
                      </button>
                    </div>

                    {/* Sub-standards (expanded) */}
                    {expanded && hasSubStandards && (
                      <div className="ml-6 mt-1 space-y-1 pb-1">
                        {subCodes.map(subCode => (
                          <label
                            key={subCode}
                            className="flex items-start gap-2 px-3 py-1.5 rounded cursor-pointer hover:bg-slate-700/50 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={standards.includes(subCode)}
                              onChange={() => toggleSubStandard(subCode)}
                              className="mt-0.5 accent-ai-accent"
                            />
                            <span className="text-xs">
                              <span className={`font-mono font-semibold ${standards.includes(subCode) ? 'text-ai-accent' : 'text-slate-300'}`}>
                                {subCode}
                              </span>
                              <span className="text-slate-400 ml-1.5 font-sans">{std.subs[subCode]}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {standards.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {standards.map(s => (
                  <span key={s} className="px-2 py-0.5 bg-ai-accent/20 text-ai-accent rounded text-[10px] font-mono">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-1">Due Date (optional)</label>
            <input
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white"
            />
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-1">AI Feedback Release</label>
            <div className="flex gap-2" role="radiogroup" aria-label="AI feedback release mode">
              <button
                type="button"
                role="radio"
                aria-checked={reviewMode === 'review'}
                onClick={() => setReviewMode('review')}
                className={`flex-1 rounded-lg border px-3 py-2 text-left text-sm ${
                  reviewMode === 'review'
                    ? 'border-teal-flow bg-teal-flow/10 text-white'
                    : 'border-slate-700 bg-slate-900 text-slate-400'
                }`}
              >
                <span className="font-medium block">Review first (recommended)</span>
                <span className="text-xs text-slate-500">AI feedback waits in FlowGrade until you release it</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={reviewMode === 'instant'}
                onClick={() => setReviewMode('instant')}
                className={`flex-1 rounded-lg border px-3 py-2 text-left text-sm ${
                  reviewMode === 'instant'
                    ? 'border-teal-flow bg-teal-flow/10 text-white'
                    : 'border-slate-700 bg-slate-900 text-slate-400'
                }`}
              >
                <span className="font-medium block">Instant</span>
                <span className="text-xs text-slate-500">Students see feedback right away (flagged items still wait)</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-1">Rubric</label>
            {rubric ? (
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 space-y-2">
                <input
                  type="text"
                  value={rubric.title}
                  onChange={(e) => setRubric(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Rubric title"
                  aria-label="Rubric title"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm"
                />
                <div className="space-y-1">
                  {rubric.criteria.map((criterion, cIdx) => {
                    const expanded = expandedCriterion === cIdx;
                    return (
                      <div key={cIdx} className="border border-slate-700 rounded-lg overflow-hidden">
                        <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-800/60">
                          <button
                            type="button"
                            onClick={() => setExpandedCriterion(prev => prev === cIdx ? null : cIdx)}
                            aria-label={expanded ? `Collapse criterion ${cIdx + 1}` : `Expand criterion ${cIdx + 1}`}
                            className="text-flow-blue-hover hover:text-flow-blue-hover/80 transition-colors flex-shrink-0"
                          >
                            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                          <input
                            type="text"
                            value={criterion.criterion}
                            onChange={(e) => updateRubricCriterionName(cIdx, e.target.value)}
                            placeholder="Criterion name"
                            aria-label={`Criterion ${cIdx + 1} name`}
                            className="flex-1 bg-transparent text-white text-sm font-medium focus:outline-none focus:bg-slate-800 rounded px-1 py-0.5"
                          />
                        </div>
                        {expanded && (
                          <div className="p-2 space-y-2">
                            {criterion.levels.map((level, lIdx) => (
                              <div key={level.score}>
                                <span className={`inline-block mb-1 px-2 py-0.5 rounded-full text-xs ${
                                  level.score === 4 ? 'bg-emerald-900/50 text-emerald-400' :
                                  level.score === 3 ? 'bg-teal-900/50 text-teal-400' :
                                  level.score === 2 ? 'bg-amber-900/50 text-amber-400' :
                                  'bg-red-900/50 text-red-400'
                                }`}>
                                  {level.score} - {level.label}
                                </span>
                                <textarea
                                  value={level.description}
                                  onChange={(e) => updateRubricLevelDescription(cIdx, lIdx, e.target.value)}
                                  rows={2}
                                  placeholder={`${level.label} description...`}
                                  aria-label={`${criterion.criterion || `Criterion ${cIdx + 1}`} - ${level.label} description`}
                                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-300 text-xs resize-y"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <DemoChip label="Sign in to generate rubrics">
                    <button
                      type="button"
                      onClick={handleGenerateRubric}
                      disabled={isGeneratingRubric}
                      className="btn-purple px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {isGeneratingRubric ? (
                        <>
                          <Loader2 className="animate-spin" size={14} />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} />
                          Generate with AI
                        </>
                      )}
                    </button>
                  </DemoChip>
                  <button
                    type="button"
                    onClick={() => { setRubric(null); setExpandedCriterion(null); }}
                    className="text-slate-400 hover:text-red-400 text-sm px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
                  >
                    Remove rubric
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 space-y-2">
                {savedRubrics.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) handleReuseRubric(e.target.value); }}
                    aria-label="Reuse a saved rubric"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-slate-300 text-sm"
                  >
                    <option value="">Reuse a saved rubric…</option>
                    {savedRubrics.map(r => (
                      <option key={r.id} value={r.id}>{r.title || 'Untitled rubric'}</option>
                    ))}
                  </select>
                )}
                <div className="flex items-center justify-between gap-3">
                <p className="text-slate-500 text-sm">Attach a rubric your feedback will score against</p>
                <DemoChip label="Sign in to generate rubrics">
                  <button
                    type="button"
                    onClick={handleGenerateRubric}
                    disabled={isGeneratingRubric}
                    className="btn-purple px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0"
                  >
                    {isGeneratingRubric ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} />
                        Generate with AI
                      </>
                    )}
                  </button>
                </DemoChip>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-700 text-white py-2 rounded-lg hover:bg-slate-600"
            >
              Cancel
            </button>
            <DemoChip label="Sign in to create assignments">
              <button
                type="submit"
                data-testid="submit-assignment-btn"
                disabled={isSubmitting}
                className="flex-1 btn-teal py-2 rounded-lg disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Create Assignment'}
              </button>
            </DemoChip>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
